import React, { useEffect, useMemo, useState } from "react";
import { api, speak } from "@/lib/kielo";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Volume2, Check, X, ArrowRight, Headphones } from "lucide-react";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

export default function Listening() {
  const { t, lang } = useI18n();
  const [sentences, setSentences] = useState([]);
  const [order, setOrder] = useState([]);
  const [pos, setPos] = useState(0);
  const [options, setOptions] = useState([]);
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState(0);

  useEffect(() => {
    api.get("/sentences", { params: { lang } }).then(({ data }) => {
      setSentences(data);
      setOrder(shuffle(data.map((_, i) => i)));
    });
  }, [lang]);

  const current = sentences.length ? sentences[order[pos]] : null;

  useEffect(() => {
    if (current) {
      const distractors = shuffle(sentences.filter((s) => s.id !== current.id)).slice(0, 3);
      setOptions(shuffle([current, ...distractors]));
      setPicked(null);
      const tm = setTimeout(() => speak(current.fi), 400);
      return () => clearTimeout(tm);
    }
  }, [current]);

  if (!current) return <div className="max-w-2xl mx-auto px-4 py-20 text-[#52635B]">{t("common.loading")}</div>;

  const pick = (opt) => {
    if (picked) return;
    setPicked(opt);
    if (opt.id === current.id) setScore((s) => s + 1);
  };
  const next = () => {
    if (pos + 1 < order.length) setPos(pos + 1);
    else { setPos(0); setOrder(shuffle(order)); }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 md:py-14" data-testid="listening-drill">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0E382B]">{t("hub.listen")}</h1>
        <span className="text-sm font-medium text-[#52635B]">{t("game.score")}: {score}</span>
      </div>

      <div className="rounded-3xl bg-white border border-[#E9E6DF] p-6 sm:p-8 text-center">
        <button
          onClick={() => speak(current.fi)}
          data-testid="listening-play"
          className="mx-auto w-20 h-20 rounded-full bg-[#0E382B] text-white flex items-center justify-center hover:bg-[#1C5442] transition-colors"
        >
          <Volume2 className="w-9 h-9" />
        </button>
        <p className="mt-3 text-sm text-[#52635B] flex items-center justify-center gap-1.5"><Headphones className="w-4 h-4" /> {t("game.playAudio")}</p>
        <p className="mt-6 font-medium text-[#0E382B]">{t("game.whatHeard")}</p>

        <div className="mt-4 grid gap-3">
          {options.map((opt) => {
            let cls = "bg-white border-[#E2DFD7] text-[#0E382B] hover:border-[#0E382B]";
            if (picked) {
              if (opt.id === current.id) cls = "bg-[#E4F0E9] border-[#Bfe0cf] text-[#0E5A35]";
              else if (opt.id === picked.id) cls = "bg-[#FBE3E3] border-[#e7b3b3] text-[#7A2E2E]";
              else cls = "bg-white border-[#E2DFD7] text-[#B5B0A2]";
            }
            return (
              <button
                key={opt.id}
                onClick={() => pick(opt)}
                data-testid={`listening-option-${opt.id}`}
                disabled={!!picked}
                className={`rounded-2xl px-4 py-3.5 text-left font-medium border transition-all flex items-center gap-2 ${cls}`}
              >
                {picked && opt.id === current.id && <Check className="w-5 h-5" />}
                {picked && opt.id === picked.id && opt.id !== current.id && <X className="w-5 h-5" />}
                {opt.en}
              </button>
            );
          })}
        </div>

        {picked && (
          <div className="mt-6">
            <p className="text-[#0E382B] font-serif text-lg italic">“{current.fi}”</p>
            <Button onClick={next} data-testid="listening-next" className="mt-4 rounded-full h-11 px-7 bg-[#0E382B] hover:bg-[#1C5442] text-white">
              {t("game.next")} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
